import {Component, OnInit} from '@angular/core';
import {EventResponse, EventTypeResponse, CompanyResponse, EventClassResponse, EventServiceResponse} from '../../@shared/models/response.module';
import {EventsService} from '../../@shared/services/events-service';
import {AQH_EVE_0, AQH_EVE_4, AQH_EVE_6, AQH_EVE_11, COR_COY_4} from '../../@shared/models/input.module';
import {formatDateDMYDots} from '../../@core/utils/utils.service';
import {CompanyService} from '../../@shared/services/company.service';

@Component({
  selector: 'app-shows',
  standalone: false,
  templateUrl: './shows.html',
  styleUrl: './shows.css'
})
export class Shows implements OnInit {
  events: EventResponse[] = [];
  filteredEvents: EventResponse[] = [];
  upcomingEvents: EventResponse[] = [];
  pastEvents: EventResponse[] = [];
  eventTypes: EventTypeResponse[] = [];
  companies: CompanyResponse[] = [];

  // Filters
  searchQuery: string = '';
  selectedEventTypeId: number | null = null;
  selectedCompanyId: string | null = null;

  // Loading states
  loading: boolean = false;

  // Event classes and services data (keyed by event_id)
  eventClasses: { [eventId: number]: EventClassResponse[] } = {};
  eventServices: { [eventId: number]: EventServiceResponse[] } = {};
  loadingClasses: { [eventId: number]: boolean } = {};
  loadingServices: { [eventId: number]: boolean } = {};

  constructor(
    private eventsService: EventsService,
    private companyService: CompanyService
  ) {}

  ngOnInit() {
    this.loadEventTypes();
    this.loadCompanies();
    this.loadEvents();
  }

  loadEvents() {
    this.loading = true;
    const params: AQH_EVE_0 = {
      // Don't filter by company_id to get all events
    };

    this.eventsService.getEvents(params).subscribe({
      next: (response) => {
        this.events = response.data || [];
        this.sortEventsByDate();
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  loadEventTypes() {
    const params: AQH_EVE_4 = {};
    this.eventsService.getEventTypes(params).subscribe({
      next: (types) => {
        this.eventTypes = types || [];
      }
    });
  }

  loadCompanies() {
    const params: COR_COY_4 = {};
    this.companyService.getAllCompanies(params).subscribe({
      next: (response) => {
        this.companies = response.data || [];
      }
    });
  }

  sortEventsByDate() {
    this.events.sort((a, b) => {
      const dateA = a.start_date ? new Date(a.start_date) : new Date(8640000000000000); // Max date if no start_date
      const dateB = b.start_date ? new Date(b.start_date) : new Date(8640000000000000);

      // Sort by earliest date first (ascending order)
      return dateA.getTime() - dateB.getTime();
    });
  }

  applyFilters() {
    this.filteredEvents = this.events.filter(event => {
      // Search filter (event name, description, city)
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch =
          event.event_name?.toLowerCase().includes(query) ||
          event.event_description?.toLowerCase().includes(query) ||
          event.city?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Event type filter
      if (this.selectedEventTypeId !== null) {
        if (event.event_type_id !== this.selectedEventTypeId) return false;
      }

      // Company filter - Note: EventResponse may need company_id field added
      if (this.selectedCompanyId) {
        // TODO: Filter by company when company_id is added to EventResponse
      }

      return true;
    });

    // Separate into upcoming and past events
    this.upcomingEvents = this.filteredEvents.filter(event => this.isEventUpcoming(event.start_date));
    this.pastEvents = this.filteredEvents.filter(event => this.isEventPast(event.start_date));
  }

  onSearchChange() {
    this.applyFilters();
  }

  onEventTypeChange() {
    this.applyFilters();
  }

  onCompanyChange() {
    this.applyFilters();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedEventTypeId = null;
    this.selectedCompanyId = null;
    this.applyFilters();
  }

  getEventTypeDisplay(eventTypeId: number): string {
    const eventType = this.eventTypes.find(t => t.event_type_id === eventTypeId);
    return eventType?.event_type_name || 'Unknown';
  }

  getEventTypeBadgeClass(eventTypeId: number): string {
    const eventType = this.eventTypes.find(t => t.event_type_id === eventTypeId);
    const typeName = eventType?.event_type_name?.toLowerCase() || '';

    if (typeName.includes('show')) {
      return 'badge-success-dark'; // Dark Green
    } else if (typeName.includes('clinic')) {
      return 'badge-primary'; // Blue
    } else if (typeName.includes('training')) {
      return 'badge-secondary'; // Gray
    }

    return 'badge-primary'; // Default blue
  }

  isEventUpcoming(startDate: Date | null | undefined): boolean {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(startDate);
    return eventDate >= today;
  }

  isEventPast(startDate: Date | null | undefined): boolean {
    if (!startDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(startDate);
    return eventDate < today;
  }

  loadEventClasses(eventId: number) {
    // Only load if not already loaded or loading
    if (this.eventClasses[eventId] || this.loadingClasses[eventId]) {
      return;
    }

    this.loadingClasses[eventId] = true;
    const params: AQH_EVE_6 = {
      event_id: eventId
    };

    this.eventsService.getEventClasses(params).subscribe({
      next: (classes) => {
        this.eventClasses[eventId] = classes || [];
        this.loadingClasses[eventId] = false;
      },
      error: () => {
        this.loadingClasses[eventId] = false;
        this.eventClasses[eventId] = [];
      }
    });
  }

  loadEventServices(eventId: number) {
    // Only load if not already loaded or loading
    if (this.eventServices[eventId] || this.loadingServices[eventId]) {
      return;
    }

    this.loadingServices[eventId] = true;
    const params: AQH_EVE_11 = {
      event_id: eventId
    };

    this.eventsService.getEventServices(params).subscribe({
      next: (services) => {
        this.eventServices[eventId] = services || [];
        this.loadingServices[eventId] = false;
      },
      error: () => {
        this.loadingServices[eventId] = false;
        this.eventServices[eventId] = [];
      }
    });
  }

  getGoogleMapsUrl(event: EventResponse): string {
    // Build the full address string
    const addressParts = [
      event.address,
      event.postal_code,
      event.city,
      event.country
    ].filter(part => part); // Remove empty parts

    const fullAddress = addressParts.join(', ');

    // Encode the address for URL
    const encodedAddress = encodeURIComponent(fullAddress);

    // Return Google Maps search URL
    return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  }

  protected readonly formatDateDMYDots = formatDateDMYDots;
}
